<template>
  <div class="admin-role-detail bg-white-custom border px-3 py-3 d-flex flex-column gap-3">
    <b-overlay :show="isReady" rounded="sm">
      <div v-if="currentRole.system" class="mb-3">
        <b-icon icon="info-circle"></b-icon>&nbsp;
        This is a system role and cannot be modified.
      </div>
      <b-form-group
        id="role-name"
        label="Role Name:"
        style="font-weight:500"
        label-for="input-role-name"
        class="required-form-field"
      >
        <b-form-input
          id="input-role-name"
          v-model="roleName"
          trim
          :disabled="currentRole.system"
        />
        <b-form-invalid-feedback :state="!roleName ? false : true">
          {{ 'Role name is required.' }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        id="permissions-field"
        label="Permissions:"
        style="font-weight:500"
        label-for="input-permissions"
        class="required-form-field"
      >
        <div id="input-permissions" class="permissions-list d-flex flex-column gap-4 py-3">
          <div
            v-for="permissionCategory in permissionsList"
            :key="permissionCategory.category"
            class="permission-category border d-flex flex-column gap-2"
          >
            <span
              class="permission-category--title text-capitalize"
              :class="{
                dark: isDarkMode
              }"
            >
              {{ permissionCategory.category }}
            </span>
            <div
              v-for="item in permissionCategory.permissions"
              :key="item.permission"
              class="form-check form-switch"
            >
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                :id="`permission:${item.permission}`"
                :name="`permission:${item.permission}`"
                :checked="checkedPermission(item.permission)"
                :disabled="currentRole.system"
                @change="clickPermission(item.permission)"
              />
              <label class="form-check-label" :for="`permission:${item.permission}`">
                {{ item.label }} (<code>{{ item.permission }}</code>)
              </label>
            </div>
          </div>
        </div>
        <b-form-invalid-feedback :state="isInvalidPermissions ? false : true">
          {{ 'At leaset one permission should be selected.' }}
        </b-form-invalid-feedback>
      </b-form-group>
      <div v-if="!currentRole.system" class="d-flex align-items-center justify-content-end gap-2">
        <b-button
          variant="success"
          pill
          size="sm"
          :disabled="savePending || deletePending"
          @click="saveRole()"
        >
          <b-spinner v-if="savePending" small></b-spinner>
          <span v-else>
            {{ roleId ? 'Save' : 'Add' }}
          </span>
        </b-button>
        <b-button
          v-if="roleId"
          pill
          variant="danger"
          size="sm"
          :disabled="savePending || deletePending"
          @click="removeRole()"
        >
          <b-spinner v-if="deletePending" small></b-spinner>
          <span v-else>Delete</span>
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import errorHandler from "@/mixins/errorHandler";
export default {
  name: 'AdminRoleDetail',
  mixins: [errorHandler],
  props: {
    roleId: {
      type: String,
      default: "",
    }
  },
  data() {
    return {
      isReady: false,
      isLoading: false,
      isLoadingPermissions: false,
      permissions: [],
      permissionsList: [],
      permissionTouched: false,
      roleName: null,
      currentRole: {},
      errors: {},
      errorMsg: null,
      deletePending: false,
      savePending: false,
      fetchAbortController: null,
      fetchAbortControllerPermissions: null,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgencyId"]),
    ...mapGetters("user", ["isDarkMode"]),
    isInvalidPermissions() {
      return this.permissions.filter(item => item.checked).length === 0;
    },
  },
  async created() {
    this.isReady = true;
    await this.fetchPermissions();
    if (this.roleId) {
      await this.fetchRoleInfo(this.roleId)
    }
    this.isReady = false;
  },
  methods: {
    async fetchRoleInfo(roleId) {
      try {
        this.fetchAbortController = new AbortController();
        this.isLoading = true;
        const resp = await this.$ConveyApi.agenciesApi.getAgencyRoleById({
          roleId,
          agencyId: this.currentAgencyId,
          signal: this.fetchAbortController.signal
        });
        this.currentRole = { ...resp.data.data };
        this.roleName = this.currentRole.role;
        this.permissions.forEach((item) => {
          item.checked = this.currentRole.permissions.findIndex(rp => rp === item.permission) >= 0;
        });
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminRoleDetail:fetchRoleInfo] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPermissions() {
      try {
        this.fetchAbortControllerPermissions = new AbortController();
        this.isLoadingPermissions = true;
        const resp = await this.$ConveyApi.authApi.getPermissions({
          signal: this.fetchAbortControllerPermissions.signal
        });
        const response = { ...resp.data.data };
        Object.keys(response).forEach(key => {
          const category = key.replaceAll('_', ' ');
          const permissions = [];
          Object.keys(response[key]).forEach(permission => {
            const label = response[key][permission];
            permissions.push({ label, permission });
            this.permissions.push({ permission, checked: false });
          });
          this.permissionsList.push({ category, permissions });
        });
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminRoleDetail:fetchPermissions] ERROR: ", err);
        }
      } finally {
        this.isLoadingPermissions = false;
      }

    },
    clickPermission(permission) {
      this.permissionTouched = true;
      const targetPermission = this.permissions.find(item => item.permission === permission);
      if (targetPermission) {
        targetPermission.checked = !targetPermission.checked;
      }
    },
    checkedPermission(permission) {
      const targetPermission = this.permissions.find(item => item.permission === permission);
      if (targetPermission) {
        return targetPermission.checked;
      }
      return false;
    },
    async saveRole() {
      this.permissionTouched = true;
      if (this.roleName && this.permissions.filter(item => item.checked).length > 0) {
        const payload = {
          role: this.roleName,
          permissions: this.permissions.filter(item => item.checked).map(item => item.permission)
        };
        if (this.roleId) {
          try {
            this.savePending = true;
            await this.$ConveyApi.agenciesApi.updateAgencyRole({
              agencyId: this.currentAgencyId,
              roleId: this.roleId,
              payload
            });
            this.$toastr("success", "Role has been updated.");
            setTimeout(() => {
              this.$emit('updated');
            }, 500);
          } catch(err) {
            console.log('[AdminRoleDetail:UpdateRole] ERROR: ', err);
            this.$toastr("error", "Error occurred updating role.");
          } finally {
            this.savePending = false;
          }
        } else {
          try {
            this.savePending = true;
            await this.$ConveyApi.agenciesApi.createAgencyRole({
              agencyId: this.currentAgencyId,
              payload
            });
            this.$toastr("success", "New role has been added.");
            setTimeout(() => {
              this.$emit('created');
            }, 500);
          } catch(err) {
            if (err.status === 400 && err.response.data && err.response.data.data && err.response.data.data.error) {
              this.$toastr("error", err.response.data.data.error);
            } else {
              this.$toastr("error", "Error occurred adding new role.");
            }
            console.log('[AdminRoleDetail:CreateRole] ERROR: ', err);
          } finally {
            this.savePending = false;
          }
        }
      }
    },
    async removeRole() {
      if (this.roleId) {
        if (confirm("Are you sure you want to remove this role from your agency?") != true)
          return;

        try {
          this.deletePending = true;
          await this.$ConveyApi.agenciesApi.removeAgencyRole({
            agencyId: this.currentAgencyId,
            roleId: this.roleId
          });
          this.$toastr("success", "Role has been removed from your agency.");
          setTimeout(() => {
            this.$emit('deleted');
          }, 500);
        } catch(err) {
          if (err.status === 400 && err.response.data && err.response.data.data && err.response.data.data.error) {
            this.$toastr("error", err.response.data.data.error);
          } else {
            this.$toastr("error", "Error occurred removing role.");
          }
          console.log('[AdminRoleDetail:DeleteRole] ERROR: ', err);
        } finally {
          this.deletePending = false;
        }
      }
    },
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
      if (this.fetchAbortControllerPermissions) {
        this.fetchAbortControllerPermissions.abort();
        this.fetchAbortControllerPermissions = null;
      }
    },
  },
  beforeDestroy() {
    this.abortFetchOperation();
  },
};
</script>

<style lang="scss" scoped>
.admin-role-detail {
  max-width: 800px;
  width: 100%;
  height: auto;
  border-radius: 1rem;

  .permissions-list {
    width: 100%;
    height: auto;
    max-height: 600px;
    overflow-y: auto;

    .permission-category {
      position: relative;
      border-radius: 1rem;
      padding: 1rem;

      &--title {
        position: absolute;
        left: 1rem;
        top: -0.75rem;
        padding: 0 0.5rem;
        background-color: white;
        &.dark {
          background-color: #272B36;
        }
      }
    }
  }
}
</style>